/**
 * BreadcrumbsExamples.tsx
 * Comprehensive examples of HeroUI Breadcrumbs component suite
 * 
 * Showcases all HeroUI Breadcrumbs features with routing integration,
 * variants, colors, collapsible behavior, and theme integration
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Breadcrumbs, BreadcrumbItem } from '@enfomentis/ui';

import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { Home, FolderOpen, FileText, Settings, User } from 'lucide-react';


export const BreadcrumbsExamples: React.FC = () => {

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Breadcrumbs */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Navigation</h3>
          <Card>
            <CardBody className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Simple breadcrumb trail</h4>
                <Breadcrumbs>
                  <BreadcrumbItem>Home</BreadcrumbItem>
                  <BreadcrumbItem>Products</BreadcrumbItem>
                  <BreadcrumbItem>Electronics</BreadcrumbItem>
                  <BreadcrumbItem>Smartphones</BreadcrumbItem>
                </Breadcrumbs>
              </div>
              
              <Divider />
              
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">With icons</h4>
                <Breadcrumbs>
                  <BreadcrumbItem startContent={<Home className="w-4 h-4" />}>
                    Dashboard
                  </BreadcrumbItem>
                  <BreadcrumbItem startContent={<FolderOpen className="w-4 h-4" />}>
                    Projects
                  </BreadcrumbItem>
                  <BreadcrumbItem startContent={<FileText className="w-4 h-4" />}>
                    Trading Journal
                  </BreadcrumbItem>
                </Breadcrumbs>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Variants */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Variants</h3>
          <div className="space-y-4">
            
            <Card>
              <CardHeader>
                <h4 className="text-sm font-medium text-text-secondary">Solid variant</h4>
              </CardHeader>
              <CardBody>
                <Breadcrumbs variant="solid" color="primary">
                  <BreadcrumbItem>Trading</BreadcrumbItem>
                  <BreadcrumbItem>Sessions</BreadcrumbItem>
                  <BreadcrumbItem>Current Session</BreadcrumbItem>
                </Breadcrumbs>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-sm font-medium text-text-secondary">Bordered variant</h4>
              </CardHeader>
              <CardBody>
                <Breadcrumbs variant="bordered">
                  <BreadcrumbItem>Analytics</BreadcrumbItem>
                  <BreadcrumbItem>Performance</BreadcrumbItem>
                  <BreadcrumbItem>Risk Metrics</BreadcrumbItem>
                </Breadcrumbs>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-sm font-medium text-text-secondary">Light variant</h4>
              </CardHeader>
              <CardBody>
                <Breadcrumbs variant="light">
                  <BreadcrumbItem>Settings</BreadcrumbItem>
                  <BreadcrumbItem>Account</BreadcrumbItem>
                  <BreadcrumbItem>Preferences</BreadcrumbItem>
                </Breadcrumbs>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Colors</h3>
          <Card>
            <CardBody className="space-y-4">
              
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Primary color</h4>
                <Breadcrumbs color="primary">
                  <BreadcrumbItem>Home</BreadcrumbItem>
                  <BreadcrumbItem>Portfolio</BreadcrumbItem>
                  <BreadcrumbItem>Analysis</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Secondary color</h4>
                <Breadcrumbs color="secondary">
                  <BreadcrumbItem>Trading</BreadcrumbItem>
                  <BreadcrumbItem>Strategies</BreadcrumbItem>
                  <BreadcrumbItem>Backtesting</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Success color</h4>
                <Breadcrumbs color="success">
                  <BreadcrumbItem>Reports</BreadcrumbItem>
                  <BreadcrumbItem>Profitable Trades</BreadcrumbItem>
                  <BreadcrumbItem>Summary</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Warning color</h4>
                <Breadcrumbs color="warning">
                  <BreadcrumbItem>Risk Management</BreadcrumbItem>
                  <BreadcrumbItem>Alerts</BreadcrumbItem>
                  <BreadcrumbItem>High Risk Positions</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Danger color</h4>
                <Breadcrumbs color="danger">
                  <BreadcrumbItem>Account</BreadcrumbItem>
                  <BreadcrumbItem>Security</BreadcrumbItem>
                  <BreadcrumbItem>Critical Settings</BreadcrumbItem>
                </Breadcrumbs>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Sizes</h3>
          <Card>
            <CardBody className="space-y-4">
              
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Small size</h4>
                <Breadcrumbs size="sm">
                  <BreadcrumbItem>Dashboard</BreadcrumbItem>
                  <BreadcrumbItem>Analytics</BreadcrumbItem>
                  <BreadcrumbItem>Small Size</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Medium size (default)</h4>
                <Breadcrumbs size="md">
                  <BreadcrumbItem>Dashboard</BreadcrumbItem>
                  <BreadcrumbItem>Analytics</BreadcrumbItem>
                  <BreadcrumbItem>Medium Size</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Large size</h4>
                <Breadcrumbs size="lg">
                  <BreadcrumbItem>Dashboard</BreadcrumbItem>
                  <BreadcrumbItem>Analytics</BreadcrumbItem>
                  <BreadcrumbItem>Large Size</BreadcrumbItem>
                </Breadcrumbs>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Collapsible Breadcrumbs */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Collapsible Breadcrumbs</h3>
          <div className="space-y-4">
            
            <Card>
              <CardHeader>
                <h4 className="text-sm font-medium text-text-secondary">Max 3 items visible</h4>
              </CardHeader>
              <CardBody>
                <Breadcrumbs maxItems={3}>
                  <BreadcrumbItem>Home</BreadcrumbItem>
                  <BreadcrumbItem>Trading</BreadcrumbItem>
                  <BreadcrumbItem>Sessions</BreadcrumbItem>
                  <BreadcrumbItem>Current Session</BreadcrumbItem>
                  <BreadcrumbItem>Trade Details</BreadcrumbItem>
                  <BreadcrumbItem>Analysis</BreadcrumbItem>
                </Breadcrumbs>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-sm font-medium text-text-secondary">Custom collapse configuration</h4>
              </CardHeader>
              <CardBody>
                <Breadcrumbs 
                  maxItems={4}
                  itemsBeforeCollapse={1}
                  itemsAfterCollapse={2}
                >
                  <BreadcrumbItem>Root</BreadcrumbItem>
                  <BreadcrumbItem>Level 1</BreadcrumbItem>
                  <BreadcrumbItem>Level 2</BreadcrumbItem>
                  <BreadcrumbItem>Level 3</BreadcrumbItem>
                  <BreadcrumbItem>Level 4</BreadcrumbItem>
                  <BreadcrumbItem>Level 5</BreadcrumbItem>
                  <BreadcrumbItem>Current Level</BreadcrumbItem>
                </Breadcrumbs>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Interactive Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Interactive Navigation</h3>
          <Card>
            <CardHeader>
              <h4 className="text-sm font-medium text-text-secondary">Clickable breadcrumbs with routing</h4>
            </CardHeader>
            <CardBody className="space-y-4">
              
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Playground navigation example</h4>
                <Breadcrumbs>
                  <BreadcrumbItem 
                    href="/playground"
                    startContent={<Home className="w-4 h-4" />}
                  >
                    Playground
                  </BreadcrumbItem>
                  <BreadcrumbItem href="/playground/components">
                    Components
                  </BreadcrumbItem>
                  <BreadcrumbItem isCurrent>
                    Breadcrumbs Examples
                  </BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Settings navigation</h4>
                <Breadcrumbs>
                  <BreadcrumbItem 
                    href="/settings"
                    startContent={<Settings className="w-4 h-4" />}
                  >
                    Settings
                  </BreadcrumbItem>
                  <BreadcrumbItem 
                    href="/settings/account"
                    startContent={<User className="w-4 h-4" />}
                  >
                    Account
                  </BreadcrumbItem>
                  <BreadcrumbItem isCurrent>
                    Profile Settings
                  </BreadcrumbItem>
                </Breadcrumbs>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Underline Styles */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Underline Styles</h3>
          <Card>
            <CardBody className="space-y-4">
              
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">No underline</h4>
                <Breadcrumbs underline="none">
                  <BreadcrumbItem>Home</BreadcrumbItem>
                  <BreadcrumbItem>Products</BreadcrumbItem>
                  <BreadcrumbItem>Current</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Hover underline</h4>
                <Breadcrumbs underline="hover">
                  <BreadcrumbItem>Dashboard</BreadcrumbItem>
                  <BreadcrumbItem>Analytics</BreadcrumbItem>
                  <BreadcrumbItem>Current</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Always underlined</h4>
                <Breadcrumbs underline="always">
                  <BreadcrumbItem>Trading</BreadcrumbItem>
                  <BreadcrumbItem>Strategies</BreadcrumbItem>
                  <BreadcrumbItem>Current</BreadcrumbItem>
                </Breadcrumbs>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Custom Separators */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Custom Separators</h3>
          <Card>
            <CardBody className="space-y-4">
              
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Custom separator</h4>
                <Breadcrumbs separator="|">
                  <BreadcrumbItem>Home</BreadcrumbItem>
                  <BreadcrumbItem>Products</BreadcrumbItem>
                  <BreadcrumbItem>Electronics</BreadcrumbItem>
                </Breadcrumbs>
              </div>

              <Divider />

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Arrow separator</h4>
                <Breadcrumbs separator="→">
                  <BreadcrumbItem>Dashboard</BreadcrumbItem>
                  <BreadcrumbItem>Analytics</BreadcrumbItem>
                  <BreadcrumbItem>Reports</BreadcrumbItem>
                </Breadcrumbs>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Usage Code Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Usage Examples</h3>
          <Card>
            <CardHeader>
              <h4 className="text-sm font-medium text-text-secondary">Import & Basic Usage</h4>
            </CardHeader>
            <CardBody>
              <pre className="text-sm text-text-secondary bg-surface p-4 rounded-lg overflow-x-auto">
{`import { Breadcrumbs, BreadcrumbItem } from '@enfomentis/ui';
import { Home, FolderOpen } from 'lucide-react';

<Breadcrumbs variant="solid" color="primary">
  <BreadcrumbItem 
    href="/dashboard"
    startContent={<Home className="w-4 h-4" />}
  >
    Dashboard
  </BreadcrumbItem>
  <BreadcrumbItem href="/components">
    Components
  </BreadcrumbItem>
  <BreadcrumbItem isCurrent>
    Current Page
  </BreadcrumbItem>
</Breadcrumbs>

// Collapsible breadcrumbs
<Breadcrumbs 
  maxItems={3}
  itemsBeforeCollapse={1}
  itemsAfterCollapse={1}
>
  <BreadcrumbItem>Long</BreadcrumbItem>
  <BreadcrumbItem>Navigation</BreadcrumbItem>
  <BreadcrumbItem>Path</BreadcrumbItem>
  <BreadcrumbItem>With</BreadcrumbItem>
  <BreadcrumbItem>Many</BreadcrumbItem>
  <BreadcrumbItem>Items</BreadcrumbItem>
</Breadcrumbs>`}
              </pre>
            </CardBody>
          </Card>
        </div>

      </div>

      {/* Bottom spacing */}
      <div className="h-20"></div>
    </PlaygroundPageLayout>
  );
};